import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { compose, lifecycle, branch, renderComponent, withProps } from 'recompose';
import { connect } from 'react-redux';

import { history } from 'store/configureStore';
import { fetchSiteLogo } from 'modules/auth/actions/getSiteLogo';
import { checkExternalInitialization } from 'shared/utils/externalLoginUtils';
import { startObserveInternetConnection } from 'modules/conference/actions/offlineHandlers';

import AppLayout from './_layouts/AppLayout';
import Page404 from './Page404';
import NoInternetPage from './NoInternetPage';
import CustomRoute from './_hocs/CustomRoute';
import PrivateRoute from './_hocs/PrivateRoute';
import DefaultPage from './_authorizationPages/DefaultPage';
import UserProfilePage from './_privatePages/UserProfilePage';
import ScheduleConferencePage from './_privatePages/ScheduleConferencePage';
import ExternalConferencePage from './_privatePages/ExternalConferencePage';
import OtpPage, { otpPageRenderCondition } from './_authorizationPages/OtpPage';
import ReschedulingOfferPage from './customPages/ReschedulingOfferPage';
import BrowserLinkLanguageInterceptor from './_hocs/BrowserLinkLanguageInterceptor';
import LoginPage, { loginPageRenderCondition } from './_authorizationPages/LoginPage';
import ExternalLoginPage from './_authorizationPages/ExternalLoginPage';
import LockdownPage, { lockdownPageRenderCondition } from './_authorizationPages/LockdownPage';
import IdentityInfoPage, { identityInfoPageRenderCondition } from './_authorizationPages/IdentityInfoPage';
import OrganizationSelectionPage, { selectOrganizationPageRenderCondition } from './_authorizationPages/OrganizationSelectionPage';
import CreateAppointmentPage from './Appointments/CreateAppointmentPage';
import CreateAppointmentSuccessPage from './Appointments/CreateAppointmentSuccessPage';
import EditAppointmentPage from './Appointments/EditAppointmentPage';


const Pages = () => (
  <ConnectedRouter history={ history }>
    <div>
      <Route path="/:language" component={ BrowserLinkLanguageInterceptor } />
      <Switch>
        <AppLayout>
          <Switch>
            <CustomRoute
              exact
              path="/"
              component={ DefaultPage }
              shouldRedirectToHomeAuthorizedUser
            />
            <CustomRoute
              exact
              path="/:language/organization-selection"
              component={ OrganizationSelectionPage }
              renderCondition={ selectOrganizationPageRenderCondition }
              redirectPath="/login"
              shouldRedirectToHomeAuthorizedUser
            />
            <CustomRoute
              exact
              path="/:language/login"
              component={ LoginPage }
              renderCondition={ loginPageRenderCondition }
              redirectPath="/organization-selection"
              shouldRedirectToHomeAuthorizedUser
            />
            <CustomRoute
              exact
              path="/:language/identity-info"
              component={ IdentityInfoPage }
              renderCondition={ identityInfoPageRenderCondition }
              redirectPath="/login"
            />
            <CustomRoute
              exact
              path="/:language/otp-verification"
              component={ OtpPage }
              renderCondition={ otpPageRenderCondition }
              redirectPath="/login"
              shouldRedirectToHomeAuthorizedUser
            />
            <CustomRoute
              exact
              path="/:language/lockdown"
              component={ LockdownPage }
              renderCondition={ lockdownPageRenderCondition }
              redirectPath="/login"
              shouldRedirectToHomeAuthorizedUser
            />
            <PrivateRoute
              exact
              path="/:language/appointments"
              component={ ScheduleConferencePage }
            />
            <PrivateRoute
              exact
              path="/:language/external-conference/:appointmentId"
              component={ ExternalConferencePage }
            />
            <PrivateRoute
              exact
              path="/:language/profile/:action?"
              component={ UserProfilePage }
            />
            <PrivateRoute
              exact
              path="/:language/appointment/create"
              component={ CreateAppointmentPage }
            />
            <PrivateRoute
              exact
              path="/:language/appointment/update/:appointmentId?"
              component={ EditAppointmentPage }
            />
            <Route
              exact
              path="/:language/rescheduling-offer/:offerId"
              component={ ReschedulingOfferPage }
            />
            <Route
              exact
              path="/:language/externalLogin"
              component={ ExternalLoginPage }
            />
            <Route
              exact
              path="/:language/noInternet"
              component={ NoInternetPage }
            />
            <Route component={ Page404 } />
          </Switch>
        </AppLayout>
      </Switch>
    </div>
  </ConnectedRouter>
);

const ExternalPages = () => (
  <ConnectedRouter history={ history }>
    <div>
      <Route path="/:language" component={ BrowserLinkLanguageInterceptor } />
      <Switch>
        <AppLayout>
          <Switch>
            <Route
              exact
              path="/:language/appointment/create"
              component={ CreateAppointmentPage }
            />
            <Route
              exact
              path="/:language/appointment/create/success"
              component={ CreateAppointmentSuccessPage }
            />
            <Route
              exact
              path="/:language/appointment/create/cancel"
              component={ () => <div>...Redirecting</div> }
            />
            <Route
              exact
              path="/:language/appointment/create/success-messaging/:appointmentId"
              component={ () => <div>...Redirecting</div> }
            />
            <Route
              exact
              path="/:language/appointment/update/:appointmentId"
              component={ EditAppointmentPage }
            />
            <Route
              exact
              path="/:language/appointment/update/:appointmentId/success"
              component={ () => <div>...Redirecting</div> }
            />
            <Route
              exact
              path="/:language/appointment/update/:appointmentId/cancel"
              component={ () => <div>...Redirecting</div> }
            />
            <Route component={ Page404 } />
          </Switch>
        </AppLayout>
      </Switch>
    </div>
  </ConnectedRouter>
);

const ExternalConferencePages = () => (
  <ConnectedRouter history={ history }>
    <div>
      <Route path="/:language" component={ BrowserLinkLanguageInterceptor } />
      <Switch>
        <AppLayout>
          <Switch>
            <Route
              exact
              path="/:language/externalLogin"
              component={ ExternalLoginPage }
            />
            <PrivateRoute
              exact
              path="/:language/external-conference"
              component={ ExternalConferencePage }
            />
            <Route component={ () => <div>...Redirecting</div> } />
          </Switch>
        </AppLayout>
      </Switch>
    </div>
  </ConnectedRouter>
);

export default compose(
  withProps(() => ({
    isExternalInit: checkExternalInitialization(),
    isReactNative: /RnUmmanu/.test(navigator.userAgent),
  })),
  connect(
    null,
    { fetchSiteLogo, startObserveInternetConnection },
  ),
  lifecycle({
    componentDidMount() {
      this.props.fetchSiteLogo();
      this.props.startObserveInternetConnection();
    },
  }),
  branch(({ isReactNative }) => isReactNative, renderComponent(ExternalConferencePages)),
  branch(({ isExternalInit }) => isExternalInit, renderComponent(ExternalPages)),
)(Pages);
